<template>
    <div class="customer-table-container">
        <table class="customer-table">
            <thead>
                <tr>
                    <th class="th-number">Sr No</th>
                    <th
                        v-for="column of columns"
                        :key="column.field"
                        :class="{
                            sortable: column.sortable !== false,
                            'sorted-primary': sortField === column.field,
                            'sorted-secondary': secondarySortField === column.field,
                        }"
                        @click="column.sortable !== false ? $emit('sort', column.field) : null"
                    >
                        {{ column.label }}
                        <span class="sort-icon" v-if="sortField === column.field">
                            <i class="material-icons">{{ sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</i>
                        </span>
                        <span class="sort-icon secondary" v-else-if="secondarySortField === column.field">
                            <i class="material-icons">{{ secondarySortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</i>
                            <span class="secondary-badge">2</span>
                        </span>
                        <span class="sort-icon sort-available" v-else-if="column.sortable !== false">
                            <i class="material-icons">unfold_more</i>
                        </span>
                    </th>
                    <th class="th-actions">Actions</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(customer, index) of customers" :key="customer.id">
                    <td class="td-number">{{ (page - 1) * limit + index + 1 }}</td>
                    <td v-for="column of columns" :key="column.field">
                        {{ displayCell(customer, column) }}
                    </td>
                    <td class="td-actions">
                        <div class="action-buttons-cell">
                            <button class="btn btn-icon edit-btn" type="button" @click="$emit('edit', customer.id)" title="Edit Customer">
                                <i class="material-icons">edit</i>
                            </button>
                            <AllowPermission :permission="PERMISSION_CODES.CMS_VIEW_LOYALTY">
                                <button
                                    class="btn btn-icon loyalty-btn"
                                    type="button"
                                    @click="$emit('manage-loyalty', customer.id)"
                                    title="Manage Points"
                                >
                                    <i class="material-icons">loyalty</i>
                                </button>
                            </AllowPermission>
                            <button class="btn btn-icon delete-btn" type="button" @click="$emit('delete', customer.id)" title="Delete Customer" :disabled="loading">
                                <i class="material-icons">delete</i>
                            </button>
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script>
import AllowPermission from "../layout/AllowPermission.vue"
import { PERMISSION_CODES } from "../../store/permission"

export default {
    components: { AllowPermission },
    props: {
        customers: { type: Array, default: () => [] },
        columns: { type: Array, default: () => [] },
        page: { type: Number, default: 1 },
        limit: { type: Number, default: 25 },
        sortField: { type: String, default: "createdAt" },
        sortDirection: { type: String, default: "desc" },
        secondarySortField: { type: String, default: "" },
        secondarySortDirection: { type: String, default: "asc" },
        loading: { type: Boolean, default: false },
    },
    data() {
        return { PERMISSION_CODES }
    },
    methods: {
        displayCell(customer, column) {
            const value = customer[column.field]
            if (column.field === "loyalty_points_balance") {
                return `${Number(value || 0)} pts`
            }
            if (column.field === "customer_group_name") {
                return value === null || value === undefined || value === "" ? "—" : value
            }
            return value === null || value === undefined || value === "" ? "-" : value
        },
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";

.customer-table-container {
    border: 1px solid $lightBorderColor;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

    .customer-table {
        width: 100%;
        border-collapse: collapse;

        th,
        td {
            padding: 10px 12px;
            text-align: left;
            font-size: 14px;
            border-bottom: 1px solid $lightBorderColor;
        }

        th {
            color: $textColor;
            font-weight: 600;
            white-space: nowrap;
            position: sticky;
            top: 0;
            z-index: 1;
            backdrop-filter: blur(2px);
            background-color: $lightCardBg;
            border-bottom: 2px solid $lightBorderColor;

            &.sortable {
                cursor: pointer;
                user-select: none;
                position: relative;
                transition: background-color 0.2s ease;

                &::after {
                    content: '';
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    height: 2px;
                    background-color: transparent;
                    transition: background-color 0.2s ease;
                }

                &:hover {
                    background-color: darken($lightCardBg, 3%);

                    &::after {
                        background-color: rgba($primaryColor, 0.3);
                    }
                }

                &.sorted-primary {
                    background-color: darken($lightCardBg, 4%);
                    font-weight: 600;

                    &::after {
                        background-color: $primaryColor;
                    }
                }

                &.sorted-secondary {
                    background-color: darken($lightCardBg, 2%);
                    font-weight: 500;

                    &::after {
                        background-color: rgba($primaryColor, 0.5);
                    }
                }

                .sort-icon {
                    display: inline-block;
                    vertical-align: middle;
                    margin-left: 4px;

                    i {
                        font-size: 16px;
                        vertical-align: text-bottom;
                    }

                    &.sort-available {
                        opacity: 0.3;
                    }

                    &.secondary {
                        opacity: 0.7;
                        position: relative;
                        margin-right: 4px;

                        .secondary-badge {
                            position: absolute;
                            top: -4px;
                            right: -10px;
                            font-size: 10px;
                            font-weight: bold;
                            background-color: rgba($primaryColor, 0.2);
                            border-radius: 50%;
                            width: 14px;
                            height: 14px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                        }
                    }
                }
            }
        }

        tr {
            &:nth-child(even) {
                background-color: rgba(0, 0, 0, 0.01);
            }

            &:hover {
                background-color: rgba($primaryColor, 0.02);
            }
        }

        .th-number,
        .td-number {
            width: 60px;
            text-align: center;
        }

        .th-actions {
            width: 140px;
            text-align: center;
        }

        .td-actions {
            text-align: center;
            white-space: nowrap;

            .action-buttons-cell {
                display: flex;
                justify-content: center;
                gap: 6px;

                .btn-icon {
                    width: 32px;
                    height: 32px;
                    padding: 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 4px;
                    border: none;
                    transition: all 0.2s ease;

                    i {
                        font-size: 16px;
                        color: #ffffff;
                    }
                }

                .edit-btn {
                    background-color: $primaryColor;

                    &:hover {
                        background-color: darken($primaryColor, 5%);
                        transform: translateY(-1px);
                        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                    }
                }

                .loyalty-btn {
                    background-color: #436f68;

                    &:hover {
                        background-color: #315750;
                        transform: translateY(-1px);
                        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                    }
                }

                .delete-btn {
                    background-color: $dangerColor;

                    &:hover {
                        background-color: darken($dangerColor, 5%);
                        transform: translateY(-1px);
                        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                    }

                    &:disabled {
                        opacity: 0.65;
                        cursor: not-allowed;
                        transform: none;
                        box-shadow: none;
                    }
                }
            }
        }
    }
}

:deep(.dark) {
    .customer-table-container {
        border-color: $darkBorderColor;

        .customer-table {
            th,
            td {
                border-color: $darkBorderColor;
                color: $darkTextColor;
            }

            th {
                background-color: $darkCardBg;
                border-bottom: 2px solid $darkBorderColor;

                &.sortable {
                    &:hover {
                        background-color: rgba(255, 255, 255, 0.08);
                    }

                    &.sorted-primary {
                        background-color: rgba($secondaryColorDark, 0.1);
                    }

                    &.sorted-secondary {
                        background-color: rgba($secondaryColorDark, 0.2);
                    }
                }
            }

            tr {
                &:nth-child(even) {
                    background-color: rgba(255, 255, 255, 0.01);
                }

                &:hover {
                    background-color: rgba(255, 255, 255, 0.02);
                }
            }
        }
    }
}

@media (max-width: 768px) {
    .customer-table-container {
        .customer-table {
            th,
            td {
                padding: 8px;
                font-size: 13px;
            }
        }
    }
}
</style>
